import BaseVisualizationService from '@configuratorware/configurator-frontendgui/App/Services/VisualizationService';
import * as apiActions from '@configuratorware/configurator-frontendgui/App/Reducers/Actions';
import { Services } from '@configuratorware/configurator-frontendgui/App/ServiceLocator';

async function fetchAsDataUri(url: string): Promise<string | null> {
  try {
    const resp = await fetch(url);
    const blob = await resp.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

const PREVIEW_WIDTH = 3600;
const PREVIEW_HEIGHT = 2400;
const CONTENT_PADDING = 0.04;

const NON_RENDERABLE_TAGS = new Set(['defs', 'style', 'script', 'title', 'desc', 'metadata']);

function computeContentViewBox(svgEl: SVGSVGElement): string | null {
  const svgBCR = svgEl.getBoundingClientRect();
  if (!svgBCR.width || !svgBCR.height) return null;

  const vb = svgEl.viewBox.baseVal;
  if (!vb.width || !vb.height) return null;

  // Scale factors: screen pixels → SVG user units
  const sx = vb.width / svgBCR.width;
  const sy = vb.height / svgBCR.height;

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

  for (const child of Array.from(svgEl.children)) {
    if (NON_RENDERABLE_TAGS.has(child.tagName.toLowerCase())) continue;
    if (child.classList?.contains('no-export')) continue;

    const bcr = child.getBoundingClientRect();
    if (!bcr.width && !bcr.height) continue;

    minX = Math.min(minX, (bcr.left - svgBCR.left) * sx + vb.x);
    minY = Math.min(minY, (bcr.top - svgBCR.top) * sy + vb.y);
    maxX = Math.max(maxX, (bcr.right - svgBCR.left) * sx + vb.x);
    maxY = Math.max(maxY, (bcr.bottom - svgBCR.top) * sy + vb.y);
  }

  if (!isFinite(minX)) return null;

  const padX = (maxX - minX) * CONTENT_PADDING;
  const padY = (maxY - minY) * CONTENT_PADDING;
  return `${minX - padX} ${minY - padY} ${(maxX - minX) + padX * 2} ${(maxY - minY) + padY * 2}`;
}

// Produces a cleaned, self-contained SVG clone ready for both PNG rasterisation
// and direct SVG output. Does NOT set width/height — callers add those for their
// specific use case (PNG needs fixed pixel dimensions; PDF uses CSS sizing).
async function buildCleanSvgClone(svgEl: SVGSVGElement): Promise<SVGSVGElement> {
  const contentViewBox = computeContentViewBox(svgEl);

  const clone = svgEl.cloneNode(true) as SVGSVGElement;

  // Remove fixed pixel dimensions — the viewBox defines the coordinate space.
  // PNG path adds them back; SVG/PDF path leaves them absent so CSS controls sizing.
  clone.removeAttribute('width');
  clone.removeAttribute('height');
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');

  if (contentViewBox) {
    clone.setAttribute('viewBox', contentViewBox);
    clone.setAttribute('preserveAspectRatio', 'xMidYMid meet');
  }

  // Strip UI overlays (rulers, selection box) — they carry className="no-export"
  clone.querySelectorAll('.no-export').forEach((el) => el.parentNode?.removeChild(el));

  // Scripts in inlined SVG content prevent canvas rendering in Chrome
  clone.querySelectorAll('script').forEach((el) => el.parentNode?.removeChild(el));

  // Remove Inkscape/RDF/DC extension elements (sodipodi:*, inkscape:*, rdf:*, cc:*, dc:*, metadata).
  // The HTML parser places all of these in the SVG namespace, so namespaceURI can't be used to
  // distinguish them — use tagName instead. Elements with ":" in the name and <metadata> are
  // non-rendering; leaving them causes XMLSerializer to emit undeclared namespace prefixes which
  // Chrome's <img> parser rejects.
  clone.querySelectorAll('metadata').forEach((el) => el.parentNode?.removeChild(el));
  Array.from(clone.querySelectorAll('*')).forEach((el) => {
    if (el.tagName.includes(':')) {
      el.parentNode?.removeChild(el);
    } else {
      // Also strip namespace-prefixed attributes (inkscape:label, sodipodi:type, etc.)
      Array.from(el.attributes)
        .filter((a) => a.name.includes(':') && !a.name.startsWith('xmlns'))
        .forEach((a) => el.removeAttribute(a.name));
    }
  });

  // Inline external <image> hrefs to avoid canvas taint from blob/cross-origin URLs.
  // If an image can't be inlined (expired blob URL, network error), remove the element
  // rather than leaving an unresolvable href which causes toDataURL() to throw SecurityError.
  const imageEls = Array.from(clone.querySelectorAll('image'));
  await Promise.all(
    imageEls.map(async (img) => {
      const href =
        img.getAttribute('href') ||
        img.getAttributeNS('http://www.w3.org/1999/xlink', 'href');
      if (!href || href.startsWith('data:')) return;
      const dataUri = await fetchAsDataUri(href);
      if (dataUri) {
        img.setAttribute('href', dataUri);
        img.removeAttributeNS('http://www.w3.org/1999/xlink', 'href');
      } else {
        img.parentNode?.removeChild(img);
      }
    })
  );

  return clone;
}

// Rasterises the clone to a PNG data URL at PREVIEW_WIDTH × PREVIEW_HEIGHT.
// Mutates the clone by setting width/height attributes needed for canvas rendering.
async function renderCloneToPng(clone: SVGSVGElement): Promise<string> {
  clone.setAttribute('width', String(PREVIEW_WIDTH));
  clone.setAttribute('height', String(PREVIEW_HEIGHT));

  const svgString = new XMLSerializer().serializeToString(clone);
  const blobUrl = URL.createObjectURL(
    new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
  );

  return new Promise<string>((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(blobUrl);
      const canvas = document.createElement('canvas');
      canvas.width = PREVIEW_WIDTH;
      canvas.height = PREVIEW_HEIGHT;
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, PREVIEW_WIDTH, PREVIEW_HEIGHT);
      ctx.drawImage(img, 0, 0, PREVIEW_WIDTH, PREVIEW_HEIGHT);
      try {
        resolve(canvas.toDataURL('image/png'));
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(blobUrl);
      reject(err);
    };
    img.src = blobUrl;
  });
}

// SVG string waiting to be uploaded once the configuration code is known
// (only needed for first-time saves where the code is assigned by the backend).
let pendingSvgString: string | null = null;

function uploadCanvasSvg(code: string, svgString: string): void {
  Services.store.dispatch(
    apiActions.postData(
      `canvas-svg/${code}`,
      svgString,
      () => ({ type: 'UPLOAD_CANVAS_SVG' }),
      { headers: { 'Content-Type': 'image/svg+xml' } }
    )
  );
}

class VisualizationService extends BaseVisualizationService {
  init() {
    // When the configuration code appears in the store (after a first-time save),
    // flush any SVG that was captured before the code was known.
    (Services.store as any).observeStore(
      (state: any) => state.configurator?.configuration?.code ?? null,
      (code: string | null) => {
        if (code && pendingSvgString) {
          uploadCanvasSvg(code, pendingSvgString);
          pendingSvgString = null;
        }
      }
    );
  }

  async getScreenshots(): Promise<Record<string, string> | null> {
    const svgEl = document.querySelector<SVGSVGElement>('[data-editor-canvas] svg');
    if (!svgEl) return null;

    try {
      // Build one clean clone shared by both output paths
      const clone = await buildCleanSvgClone(svgEl);

      // Serialise BEFORE renderCloneToPng mutates the clone with pixel dimensions
      const svgString = new XMLSerializer().serializeToString(clone);

      const pngDataUrl = await renderCloneToPng(clone);

      // Upload SVG immediately if the code is already in the store (update saves),
      // otherwise hold it for the observeStore callback (first-time saves).
      const code = (Services.store as any).state?.configurator?.configuration?.code ?? null;
      if (code) {
        uploadCanvasSvg(code, svgString);
      } else {
        pendingSvgString = svgString;
      }

      return { canvas: pngDataUrl };
    } catch (err) {
      console.error('Screenshot capture failed:', err);
      return null;
    }
  }
}

export default VisualizationService;
